import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useStore } from "zustand";
import type { ForteXApi } from "@fortex/api";
import { MapCoreProvider, errorMessage } from "@fortex/map";
import type { Theme, Toast } from "@fortex/map";
import type { AuthActions, AuthState, AuthStore } from "../auth/store";
import type { AppConfig } from "../config";
import { appLabelsFor, langIdOf } from "../i18n";
import type { AppLabels, AppLang } from "../i18n";
import { pushToast } from "./shellCore";
import type { ShellCore } from "./shellCore";

export const themeStorageKey = "fortex-web:theme";

export type AppServices = {
  config: AppConfig;
  api: ForteXApi;
  auth: AuthStore;
  shell: ShellCore;
  initialLang: AppLang;
  initialTheme: Theme;
};

export type AppContextValue = {
  config: AppConfig;
  api: ForteXApi;
  auth: AuthStore;
  shell: ShellCore;
  lang: AppLang;
  setLang: (lang: AppLang) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  labels: AppLabels;
  toast: (severity: Toast["severity"], text: string) => void;
  reportError: (error: unknown) => void;
};

export const AppContext = createContext<AppContextValue | null>(null);

export function readStoredTheme(storage: Storage | null = typeof window === "undefined" ? null : window.localStorage): Theme | null {
  try {
    const value = storage?.getItem(themeStorageKey) ?? null;

    return value === "light" || value === "dark" || value === "system" ? value : null;
  } catch {
    return null;
  }
}

export function AppProvider({ services, children }: { services: AppServices; children: ReactNode }) {
  const [lang, setLangState] = useState<AppLang>(services.initialLang);
  const [theme, setThemeState] = useState<Theme>(services.initialTheme);
  const labels = useMemo(() => appLabelsFor(lang), [lang]);

  useEffect(() => {
    services.shell.setLabels(labels);
    services.api.setLangId(langIdOf(lang));

    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [services, labels, lang]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.documentElement.dataset.theme = theme;

    try {
      window.localStorage.setItem(themeStorageKey, theme);
    } catch {
      return;
    }
  }, [theme]);

  const setLang = useCallback((next: AppLang) => setLangState(next), []);
  const setTheme = useCallback((next: Theme) => setThemeState(next), []);
  const toast = useCallback((severity: Toast["severity"], text: string) => void pushToast(services.shell, severity, text), [services]);
  const reportError = useCallback((error: unknown) => void pushToast(services.shell, "danger", errorMessage(error, labels)), [services, labels]);

  const value = useMemo<AppContextValue>(
    () => ({ config: services.config, api: services.api, auth: services.auth, shell: services.shell, lang, setLang, theme, setTheme, labels, toast, reportError }),
    [services, lang, setLang, theme, setTheme, labels, toast, reportError]
  );

  return (
    <AppContext.Provider value={value}>
      <MapCoreProvider core={services.shell}>{children}</MapCoreProvider>
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const value = useContext(AppContext);

  if (value === null) throw new Error("useApp must be used inside <AppProvider>");

  return value;
}

export function useAuth<T>(selector: (state: AuthState & AuthActions) => T): T {
  const { auth } = useApp();

  return useStore(auth, selector);
}

export function useLabels(): AppLabels {
  return useApp().labels;
}
